import { Component, computed, HostBinding, inject, input, output, signal } from '@angular/core';
import { Agent, AgentKeys } from '@ag-common-lib/lib/models/domain/agent.model';
import { Policy, PolicyKeys } from '@ag-common-lib/lib/models/utils/policy.model';
import { PolicySigners, PolicySignersKeys } from '@ag-common-lib/lib/models/utils/policy-signers.model';
import { ChangeSourceType, PolicyTypes } from '@ag-common-lib/public-api';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { PolicyService, PolicySignersService } from 'ag-common-svc/lib/services/user-agreements';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-user-agreements-acceptance-modal',
  templateUrl: './user-agreements-acceptance-modal.component.html',
  styleUrl: './user-agreements-acceptance-modal.component.scss',
})
export class UserAgreementsAcceptanceModalComponent {
  @HostBinding('class') className = 'user-agreements-acceptance-popup';

  private policySignersService = inject(PolicySignersService);
  private policyService = inject(PolicyService);

  agent = input.required<Agent>();
  popupVisible = input.required<boolean>();
  isPWA = input<boolean>(false);
  close = output();
  showUserAgreement = output<PolicyTypes>();

  inProgressSig = signal<boolean>(false);
  isCurrentUserAgreementConfirmedSig = signal<boolean>(false);
  isSubmitDisabled = computed(() => !this.isCurrentUserAgreementConfirmedSig());

  publishedMastePrivacyPolicySig = this.policyService.publishedMasterPrivacyPolicySig;
  publishedMasterTermsOfServiceSig = this.policyService.publishedMasterTermsOfServiceSig;

  protected PolicyTypes = PolicyTypes;

  protected userAgreementConfirmationChanged = e => {
    const value = e?.value;
    this.isCurrentUserAgreementConfirmedSig.set(!!value);
  };

  protected async handelSubmitClick() {
    this.inProgressSig.set(true);
    const agent = this.agent();
    const agentDbId = agent?.[BaseModelKeys.dbId];
    const currentMasterPrivacyPolicy: Policy[] = this.publishedMastePrivacyPolicySig();
    const currentMasterTermsOfService: Policy[] = this.publishedMasterTermsOfServiceSig();
    const action = this.isPWA() ? ChangeSourceType.pwaPortal : ChangeSourceType.regularPortal;

    if (!agentDbId) {
      this.inProgressSig.set(false);
      this.close.emit();
      return;
    }

    const basePolicySigner: PolicySigners = Object.assign({}, new PolicySigners(), {
      [PolicySignersKeys.agentId]: agent?.[AgentKeys.p_agent_id] ?? '',
      [PolicySignersKeys.agentEmail]: agent?.[AgentKeys.p_email] ?? '',
      [PolicySignersKeys.agentFirstName]: agent?.[AgentKeys.p_agent_first_name] ?? '',
      [PolicySignersKeys.agentLastName]: agent?.[AgentKeys.p_agent_last_name] ?? '',
      [PolicySignersKeys.agentDbId]: agentDbId,
      [PolicySignersKeys.agentMgaId]: agent?.[AgentKeys.p_mga_id] ?? '',
      [BaseModelKeys.actionName]: action,
      [BaseModelKeys.updatedDate]: new Date(),
    });

    const policiesToSign: Policy[] = [];

    if (currentMasterPrivacyPolicy?.length) {
      policiesToSign.push(currentMasterPrivacyPolicy[0]);
    }

    if (currentMasterTermsOfService?.length) {
      policiesToSign.push(currentMasterTermsOfService[0]);
    }

    try {
      const promises = policiesToSign.map(async policy => {
        const policyType = policy[PolicyKeys.type];
        const currentVersion = policy[PolicyKeys.version];

        // Skip creating a signer if the agent has already signed this version
        const lastSignedVersion = await firstValueFrom(
          this.policySignersService.getLastSignedVersionByType(agentDbId, policyType),
        );

        if (lastSignedVersion === currentVersion) {
          return;
        }

        const docDbId = policy[BaseModelKeys.dbId];
        const policySigner: PolicySigners = Object.assign({}, basePolicySigner, {
          [PolicySignersKeys.policyDbId]: docDbId,
          [PolicySignersKeys.policyType]: policyType,
          [PolicySignersKeys.policyVersion]: currentVersion,
          [PolicySignersKeys.lastSignedVersion]: lastSignedVersion ?? null,
        });

        return this.policySignersService.createSigner(docDbId, policySigner);
      });

      await Promise.all(promises);
    } catch (error) {
      console.error('error', error);
    } finally {
      this.inProgressSig.set(false);
      this.close.emit();
    }
  }

  openUserAgreementsModal(event: MouseEvent, agreementType: PolicyTypes) {
    event.preventDefault();
    const isPWA = this.isPWA();
    if (!isPWA) {
      const link = agreementType === PolicyTypes.privacyPolicy ? '/privacy-policy' : '/terms-of-service';
      window.open(link, '_blank');
      return;
    }
    this.showUserAgreement.emit(agreementType);
  }
}
